// >>> HER MEMBERSHIP SURFACE, OUT WHERE HER ITEM 3 CAN REPLACE IT. <<<
//
// MOVED VERBATIM from `src/App.tsx` (2026-08-17). It paid the R8 ceiling for REV-1's approval desk,
// and the extraction is EARNED rather than argued for (roadmap 7's own method, and R217's ruling):
// her overnight item 3 rebuilds this exact surface as a PAGE carrying her delivered artwork, tier-aware
// buttons and three rotating review slots -- and none of that is reachable while the page it replaces
// is buried inside an eleven-thousand-line file no fixture can load.
//
// It holds hooks, so it is the `TierCards` / `MicButton` / `SignInModal` class rather than a hookless
// leaf a gate CALLS. **The reach it buys is the IMPORT**, which is what lets a fixture read the one
// thing that matters about it: this is the surface every locked door on this site eventually sends a
// member to, and her ruling is that it becomes a PAGE and the old popup retires from member-facing
// surfaces once it is live.

import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { type TierCard, type LockInfo, type ValueCatalog, readTierCatalog } from "../lib/tier-catalog";
import { MembershipLander } from "./MembershipLander";
import { PageOutlineTitle } from "./PageOutlineTitle";
import { useReviews } from "./ReviewRail";
import { valueStacks } from "../membershipValue.js";
import { membershipNoteKey, landerReviews } from "../membershipPage.js";
import { DEFAULT_LANGUAGE } from "../language.js";
import { t } from "../strings.js";
import { safeSessionStorage } from "../lib/safe-storage";
import { stashPendingCheckout } from "../lib/pending-checkout";


// T1-3 / T1-6 — THE PURPLE BOX. The locked door, and it is never a cold wall.
//
// HER SPEC, three states, one component:
//   1. NOT YET SUBSCRIBED — all three tiers in full, each listing everything it includes (its own
//      unlocks PLUS "everything in" the tier below, spelled out), prices, one tap to join. She
//      chooses from right here; she is never told to go somewhere and subscribe.
//   2. SUBSCRIBED, touching something higher — name the tier that opens it, list what that upgrade
//      ADDS, one tap to upgrade.
//   3. AT THE FAIR-USE CAP — warmly: upgrade, or it comes back tomorrow.
//
// EVERYTHING IT RENDERS COMES FROM THE SERVER. The 402 refusal carries the feature, the tier that
// opens it, the whole ladder and the free layer; the tier list comes from /api/membership/tiers.
// Nothing about who-gets-what is decided here, because a second copy of that rule on the client is
// exactly the bug that made the free layer raise a box on her own birth chart.
// W1-1, then 2026-08-18: the TierCard type moved to the component that rendered it, and moved again
// to src/lib/tier-catalog.ts — the module that owns the payload it describes — when her ruling
// retired that component. A type is a different fact from a renderer (R18).
// R73: `LockInfo` moved to `src/lib/tier-catalog.ts` (imported at the top) — it is the SERVER's
// lock payload, and it belongs beside the transport that reads the other half of the same catalogue.

// T1-2 — THE MEMBERSHIP PAGE. Her box, standing on its own page.
//
// Same header, same cards, same bottom bar: it renders the SAME `TierCards` the locked-door box
// does, from the SAME server payload, so the page can never advertise a tier the gate does not open.
// The difference is only that nothing is being refused here — she came to look.
export function MembershipPage({
  goBack, language = DEFAULT_LANGUAGE, tier = null, known = false, lock = {}, rate = null,
  // >>> HER RULING, 2026-09-18 (#271) — THE CANCEL SHE COULD NOT FIND. <<<
  // The SAME portal door the account menu opens, handed in rather than reached for: never a second
  // cancel path, because two ways to end a membership is two answers to whether it ended (R28/R29).
  onManageMembership, manageBusy = false, paying = false,
  // The sign-in door for a STRANGER who pressed a tier: the checkout refuses her (401) and its
  // sentence would land below the whole ladder, out of sight on a phone, so the tap read as dead.
  onSignIn,
}: {
  goBack: () => void; language?: string; tier?: string | null; known?: boolean; lock?: LockInfo;
  rate?: { tier: string; rate: number; credit: number; heldLabel?: string } | null;
  onManageMembership?: () => void;
  manageBusy?: boolean;
  /** True only when she really has a paid subscription to end. A free member offered a cancel meets
   *  a refusal she did nothing to deserve, and a signed-out visitor is not a member at all (R18). */
  paying?: boolean;
  onSignIn?: () => void;
}) {
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const noteKey = membershipNoteKey(lock, note);
  // R91 — **UNDEFINED RATHER THAN "free".** Seeded "free", a failed read put "Your plan" on the FREE
  // card in front of a paying member; undefined marks NO card (R73: a page that does not know must
  // not claim).
  // >>> THE TIER ARRIVES AS A PROP RATHER THAN THIS PAGE REACHING BACK INTO App.tsx's CONTEXT. <<<
  // `useEntitlements` reads a context declared in the shell, and importing it here would be the
  // big-bang cascade R8's own law forbids. The ONE call site hands it over -- and that is also what
  // makes her item 3's TIER-AWARE rendering driveable by a gate with no React context at all, which
  // is the half her staged-seat-per-class proof clause needs.

  // >>> THE KNOWN-FACT ARRIVES AS AN ANSWER RATHER THAN BEING GUESSED FROM AN ABSENT TIER, AND A
  // SIGNED-OUT VISITOR IS THE REASON. <<< `tier` is null for a stranger AND null for the beat before
  // the entitlements read lands -- two facts (R18/L5.1) -- and read as one, **every control on the
  // page her advertising points at is dead for somebody who has never paid**, which is the whole
  // surface. `membershipKnown` is already the law for that question, so this is a THIRD CALLER on it
  // rather than a second answer (R28/R29, TRAP-1a's own shape), computed at the ONE call site and
  // passed in -- which is also what makes the tier-aware rendering driveable with no React context.
  const ladderTier = known ? (tier || "free") : undefined;

  // A4 — the catalogue, for the value stack alone, through `readTierCatalog` and NEVER a hand-typed
  // fetch. **R73's census went red about this round's own first draft and was right:** that door
  // carries `cache: "no-store"` because *prices are the one thing on this site a stale cache must
  // never answer*, and a second reader written here would have dropped it silently. It answers `null`
  // on any refusal, which this surface draws as nothing at all.
  const [catalog, setCatalog] = useState<ValueCatalog | null>(null);
  useEffect(() => {
    let live = true;
    readTierCatalog<TierCard, unknown>().then((answer) => { if (live) setCatalog(answer); });
    return () => { live = false; };
  }, []);

  // >>> HER MEMBERSHIP PAGE (2026-08-18): THE CATALOGUE READ WAS DELETED AT ITS DECLARATION BECAUSE
  // **NOTHING READ IT** — AND A4 (2026-08-23) GAVE IT A READER, SO IT IS BACK. <<< That ruling is
  // migrated at its declaration rather than left standing beside a live read (DL-2/DT-2b/R181): it
  // was never *this page must not read the catalogue*, it was *a line that cannot change an answer
  // is a line no reader can trust* (R216) and *a dead read is one round from a fixture certifying
  // it* (R165). Her artwork still paints every tier NAME and every PRICE, and nothing here rebuilds
  // either — what the catalogue supplies is the WHAT-YOU-GET list, which her master does not paint
  // and which `tierContents` has composed since it was written.
  //
  // **R73's LAW BINDS AGAIN AND IS ANSWERED BY ABSENCE:** a ladder this house could not read is not
  // an empty shop, so a refused or unlanded read draws NO stack at all — which is this page exactly
  // as it shipped yesterday — rather than a heading over nothing.


  // **L5.2 — THE ONE-TIME PRODUCT GOES THROUGH THE SAME DOOR AS A MEMBERSHIP, WHICH IS WHY THIS IS
  // ONE FUNCTION AND NOT TWO.** Her walk: the Spirit Keeping card had no button, no click and no
  // path to purchase — a live-money tier with no door — while `handleCreateCheckoutSession` has
  // accepted `{ product: "spirit-keeping" }` since T1-7 and its price id has been in the environment
  // since the launch flip. The card was the only missing piece. `join` takes the whole BODY now, so
  // the checkout, the portal fallthrough, the refusal sentence and the busy state are the ladder's
  // own rather than a second copy written for one card (R28/R29).
  async function join(tierOrBody: string | { product: string }, state?: string) {
    const body = typeof tierOrBody === "string" ? { tier: tierOrBody } : tierOrBody;
    setBusy(typeof tierOrBody === "string" ? tierOrBody : tierOrBody.product);
    setNote("");
    // >>> **ITEM 11(b) — A MOVE DOWN IS A DIFFERENT DOOR AND A DIFFERENT PROMISE.** <<< The checkout
    // below opens Stripe's tier-change flow, which prorates immediately: correct going UP, and
    // pointed down it takes away a room she has already paid for and invoices her for the privilege.
    // Her ruling is period-end, which is a subscription SCHEDULE, and the answer is a SENTENCE rather
    // than a redirect — she stays on this page and is told the date.
    if (state === "downgrade" && typeof tierOrBody === "string") {
      try {
        const res = await fetch("/api/subscription/downgrade", {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tier: tierOrBody }),
        });
        const data = await res.json().catch(() => ({}));
        // R47/R53/R61: the ANSWER is read. The server's own sentence is shown whether it agreed or
        // refused, because it is the only party that knows the date and the reason.
        setNote(String(data?.message || "") || t(language, "membership.checkoutFailed"));
      } catch {
        setNote(t(language, "membership.checkoutFailed"));
      }
      setBusy("");
      return;
    }
    try {
      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, returnTo: "/my-room" }),
      });
      // CHECKOUT-SIGNIN-Q1: YES (hers): the tier she pressed travels through sign-in, and the shell
      // carries her straight on to THAT checkout once she is in (src/lib/pending-checkout.ts).
      if (res.status === 401 && onSignIn) { stashPendingCheckout(safeSessionStorage(), body, Date.now()); setBusy(""); onSignIn(); return; }
      const data = await res.json().catch(() => ({}));
      if (data?.checkoutUrl) { window.location.href = data.checkoutUrl; return; }
      // T8-6: an existing member does not buy a second membership and is NEVER granted a tier by
      // this click. The server hands back Stripe's own confirmation screen for the change; nothing
      // moves until she confirms there and the webhook lands.
      if (data?.portalUrl) { window.location.href = data.portalUrl; return; }
      setNote(data?.message || t(language, "membership.checkoutFailed"));
    } catch {
      setNote(t(language, "membership.checkoutFailed"));
    }
    setBusy("");
  }

  // >>> **HER 2026-08-27 RULING: THE ARTWORK PAGE IS RETIRED AND THIS IS A LANDING PAGE.** <<<
  // Her verdict on what stood here: *"it reads as a money grab."* So the two classes that made this
  // surface stand down for a painted master come OFF, and one class that paints her white ground
  // goes on. **THAT SWAP IS ALSO WHAT ANSWERS HER "NO BROWN ANYWHERE"**: `is-stranger-home` carries
  // `--home-edge: rgb(34, 25, 21)` under 820px — a real muddy brown, kept deliberately because it
  // matched the EDGES OF HER MASTERS. The masters are gone from this page, so the reason for that
  // brown is gone with them, and it leaves by the class coming off rather than by a colour fighting
  // a colour further down the sheet.
  useEffect(() => {
    document.body.classList.add("is-membership-lander");
    return () => { document.body.classList.remove("is-membership-lander"); };
  }, []);

  // Her review pool. The read is public and buys nothing; its failure draws NOTHING, which is the
  // same ruling her home carousel carries -- a stranger deciding whether to pay is never told the
  // house's own read broke.
  //
  // >>> **AND THE PAINTED-SLOT LENGTH FILTER IS RETIRED WITH THE PAINTING (R181, at its own
  // declaration).** <<< `slotPool` existed because a quote longer than her painted box either got
  // clipped mid-sentence or spilled across her stars. There is no painted box now — every quote sets
  // in real type that wraps — so filtering her members' words by LENGTH would be discarding real
  // reviews for a constraint that no longer exists.
  const reviews = useReviews(true);
  // >>> HER ROUND-2 NOTE 4: the closet review is not for strangers. <<< `landerQuotes` drops a quote
  // that leans on a word only somebody already inside would understand, and can never empty her rail.
  // 1e: named members only, newest and strongest first, the law in membershipPage.js.
  const quotes = landerReviews(reviews) as { text: string; name: string }[];

  // The ladder her page really sells, composed by the SERVER's own composer (R28/R29). `null` is a
  // read that has not landed or has failed, and the lander draws no tier section at all on it.
  const stacks = valueStacks(catalog as never);

  return (
    <>
    {/* >>> HER ITEM 4 IS UNCHANGED AND IS NOT REBUILT: THE WAY OUT SITS ABOVE EVERYTHING. <<<
        It was the LAST element on the page once -- measured at y=1248 on desktop and y=1966 on the
        phone -- so a member who came to look had to scroll the whole page to leave. It is gold and
        it always was; what it was not is findable. **IT STAYS A SIBLING, IN FLOW, ABOVE THE PAGE**,
        which is the placement her artwork's own three-times-shipped defect paid for. What changed
        with her 2026-08-27 ruling is only the ground it stands on: gold on black is unreadable on a
        white page, so one rule gives it the ink ground and the SAME gold. */}
    <button className="memberart__back memberart__back--top" type="button" onClick={goBack}>
      <ArrowLeft size={18} />
      {t(language, "common.back")}
    </button>

    {/* R43 — the one page that asks her for money keeps the heading its outline was missing, DERIVED
        from PAGE_TITLES so the tab and the outline are one declaration. The lander's hero line is
        display type rather than a second h1, and its own comment says why. */}
    <PageOutlineTitle page="membership" />

    {/* >>> **HER LANDING PAGE.** <<< Everything it draws about the ladder -- names, prices,
        taglines, feature lines, the inheritance sentence and the turns -- comes through
        `valueStacks` off the SERVER's catalogue, which is the law `MembershipPage`'s own comment
        has carried since it was written: *a second copy of that rule on the client is exactly the
        bug that made the free layer raise a box on her own birth chart.* A prettier page is not a
        licence to re-type the ladder. */}
    <MembershipLander
      language={language}
      stacks={stacks as never}
      catalog={catalog}
      ladderTier={ladderTier}
      rate={rate}
      busy={busy}
      quotes={quotes}
      onJoin={join}
    />

    {/* >>> WHATEVER THE CHECKOUT SAID BACK, AND THE REFUSAL SHE MAY HAVE ARRIVED WITH. <<< Her
        ruling retired the popup, and the one thing it carried that a page cannot paint is L15's
        frozen face -- so `gateLock` travels here and `membershipNoteKey` decides, with the live
        checkout answer outranking it (R18). */}
    {note || noteKey ? <p className="memberart__note" role="status">{note || t(language, noteKey)}</p> : null}

    {/* =========================================================================================
        >>> **HER RULING, 2026-09-18 (#271) — SHE CAN END IT FROM THE PAGE SHE IS STANDING ON.** <<<

        The ticket said *"no cancel option, only upgrade"*, and the screen agreed with her: this page
        drew the ladder and nothing else. The cancel was real, but it lived behind the account menu,
        and **a control a member cannot find is a control she does not have.**

        THE TRUST-BRAND STANDARD, WHICH IS THE POINT OF HER RULING. It sits where she is already
        standing when she is thinking about her membership. It says what it is in plain words rather
        than hiding a cancel inside "manage your plan". **There is no guilt line, no warning about
        what she will lose, no countdown and no hoop** — and it tells her the one thing she actually
        needs to know, which is that she keeps what she has already paid for until the period ends.
        ========================================================================================= */}
    {onManageMembership && paying ? (
      <div className="memberart__manage">
        <p className="memberart__managelede">{t(language, "membership.manage.lede")}</p>
        <button
          type="button"
          className="memberart__managebtn"
          onClick={() => onManageMembership()}
          disabled={manageBusy}
        >
          {t(language, manageBusy ? "membership.manage.busy" : "membership.manage.cta")}
        </button>
        <p className="memberart__managefine">{t(language, "membership.manage.fine")}</p>
      </div>
    ) : null}
    </>
  );
}
